import { Box, Link, styled, Typography } from '@mui/material';
import { AuthError } from 'firebase/auth';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { useFormik } from 'formik';
import { forgotPassword } from '../services/firebaseAuth';
import { getAuthErrorMessage } from '../utils';
import { TextInputField } from '../components/TextInputField';
import { useNavigate } from 'react-router-dom';
import { StyledGreenButton, StyledLink, StyledRowBox } from '../theme/StyledElements';

const StyledBox = styled(Box)(() => ({
    width: '50vw',
    paddingLeft: '100px'
}));

const StyledLoginHeading = styled(Typography)(() => ({
    color: '#134C03',
    margin: '10px 0px',
    fontWeight: 'normal'
}));

const StyledLoginSubHeading = styled(Typography)(() => ({
    color: '#134C03',
    margin: '10px 0px',
    fontWeight: 'normal',
    width: '80%'
}));

export function ForgotPassword(): React.ReactElement {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [loading, setloading] = useState(false);

    const validationSchema = yup.object({
        email: yup.string().email(t('enterValidEmailMessage'))
    });

    const changePasswordForm = useFormik({
        initialValues: {
            email: ''
        },
        validationSchema,
        onSubmit: async (values) => {
            if (!changePasswordForm.isValid) return;
            setloading(true);
            const { email } = values;

            try {
                const resetPassword = await forgotPassword(email);
                if (resetPassword) {
                    changePasswordForm.setErrors(getAuthErrorMessage(resetPassword.code, t));
                } else {
                    navigate('/auth/login');
                }
            } catch (authError) {
                const error = authError as AuthError;
                changePasswordForm.setErrors(getAuthErrorMessage(error.code, t));
            }
            setloading(false);
        }
    });

    const { values, errors, handleChange, submitForm, isValid } = changePasswordForm;

    return (
        <StyledRowBox>
            <StyledBox>
                <StyledLoginHeading variant="h2">{t('forgotPassword')}</StyledLoginHeading>
                <StyledLoginSubHeading variant="h5">
                    {t('emailSentToResetPassword')}
                </StyledLoginSubHeading>
                <TextInputField
                    id="email"
                    name="email"
                    label={t('yourEmailAddress')}
                    value={values.email}
                    onChange={handleChange}
                    error={Boolean(errors.email)}
                    helperText={errors.email}
                    sx={{
                        width: '80%',
                        marginBottom: '15px',
                        backgroundColor: '#0000000B',
                        color: '#00000099',
                        font: 'normal normal normal 24px Roboto'
                    }}
                />
                <StyledGreenButton
                    loading={loading}
                    disabled={!isValid || !Boolean(values.email)}
                    type="button"
                    onClick={async (e): Promise<void> => {
                        e.preventDefault();
                        await submitForm();
                    }}
                    sx={{ margin: '40px 0px 80px 0px' }}>
                    <Typography variant="caption" color="#FFFFFF">
                        {t('iResetMyPassword')}
                    </Typography>
                </StyledGreenButton>
                <br />
                <StyledLink href="/login" variant="caption" sx={{ margin: '10px 0px' }}>
                    {t('returnToLogin')}
                </StyledLink>
            </StyledBox>
        </StyledRowBox>
    );
}
